import { CanvasModel } from "../core/canvasModel";
import type { KarelModelShape } from "../core/karelModel";
import { Karel, type Karel as KarelController } from "../runtime/karel";
import { karelImages } from "../runtime/karelImages";
import {
  KarelEvalEngine,
  type KarelEvalEngine as KarelEngine,
} from "../runtime/karelEvalEngine";
import type { KarelEditor } from "../editor/karelCodeEditor";
import { requireWorldText } from "../util/assets";
import { loadWorldModel } from "../util/worldModel";
import { showErrorToast } from "../app/stores/toastStore";

export interface KarelIde {
  ready: Promise<void>;
  stopButton(): Promise<void>;
  resizeCanvas(width: number, height: number): void;
  redraw(): void;
  playButton(playCallback?: (error?: unknown) => void): void;
  pauseButton(): void;
  stepButton(finishedCallback?: (error?: unknown) => void): Promise<void>;
  isRunning(): boolean;
  changeWorld(fileName: string): Promise<void>;
  stepMove(): void;
  stepTurnLeft(): void;
  stepTurnRight(): void;
  stepPutBeeper(): void;
  stepPickBeeper(): void;
  getModel(): KarelModelShape;
  setCode(code: string): void;
  getCode(): string | null;
  setSilent(newSilent: boolean): void;
  setLanguage(language: "js" | "python"): void;
  setInputs(inputs: string[]): void;
  getOutput(): string[];
  destroy(): void;
  runUnitTest(
    inputWorld: string,
    outputWorld: string | null,
    callback: (passed: boolean) => void,
    options?: { inputs?: string[]; expectedOutput?: string },
  ): Promise<void>;
  runSpecificCode(
    code: string,
    finishedCallback: (error?: unknown) => void,
  ): Promise<void>;
  runCode(finishedCallback?: (error?: unknown) => void): Promise<void>;
  playCallback?: (error?: unknown) => void;
}

export interface KarelIdeOptions {
  language?: "js" | "python";
  inputs?: string[];
  onOutput?: (lines: string[]) => void;
}

export function KarelIde(
  getEditor: () => KarelEditor | null,
  canvas: HTMLCanvasElement | null,
  initialWorld: string,
  options: KarelIdeOptions = {},
): KarelIde {
  let language: "js" | "python" = options.language ?? "js";
  let inputs: string[] = options.inputs ? [...options.inputs] : [];
  let outputLines: string[] = [];
  const ACTION_HEARTBEATS = 20;
  const HEART_BEAT = 5;
  const REFRESH_HEARTBEATS = 100;
  const DEFAULT_CANVAS_WIDTH = 370;
  const DEFAULT_CANVAS_HEIGHT = 370;

  const context = canvas ? canvas.getContext("2d") : null;
  let actionCountdown = ACTION_HEARTBEATS;
  const refreshCountdown = REFRESH_HEARTBEATS;
  let worldName = initialWorld;
  const canvasModel = CanvasModel(DEFAULT_CANVAS_WIDTH, DEFAULT_CANVAS_HEIGHT);
  let worldLoaded = false;

  const karel: KarelController = Karel(canvasModel);
  let compileEngine: KarelEngine | null = null;

  let animating = false;
  let imagesReady = false;
  let numActions = 0;
  let silent = false;
  let heartbeatId: ReturnType<typeof setInterval> | null = null;
  // The code string backing the staged compile engine. When it no longer
  // matches the editor contents the engine is rebuilt before the next step.
  let stepCode: string | null = null;
  let stepBusy = false;

  let readyResolve!: () => void;
  const ready = new Promise<void>((resolve) => {
    readyResolve = resolve;
  });

  const that: KarelIde = {
    ready,

    async stopButton(): Promise<void> {
      clearEngine();
      that.playCallback = undefined;
      await loadWorld(worldName);
    },

    resizeCanvas(width: number, height: number): void {
      canvasModel.resizeCanvas(width, height, worldLoaded);
      if (canvas) {
        canvas.width = canvasModel.getWidth();
        canvas.height = canvasModel.getHeight();
      }
      if (worldLoaded) {
        draw();
      }
    },

    /** Repaints the current world, e.g. after the theme palette changes. */
    redraw(): void {
      if (worldLoaded) {
        draw();
      }
    },

    playButton(playCallback?: (error?: unknown) => void): void {
      void that.runCode(playCallback);
    },

    /**
     * Stops the auto-advancing animation but keeps the compiled program so a
     * following `stepButton()` resumes from the current position.
     */
    pauseButton(): void {
      animating = false;
    },

    isRunning(): boolean {
      return animating;
    },

    /**
     * Executes a single buffered action, compiling the editor contents first
     * (resetting the world) when there is no staged program or the code has
     * changed since it was staged.
     */
    async stepButton(
      finishedCallback?: (error?: unknown) => void,
    ): Promise<void> {
      if (stepBusy) return;
      stepBusy = true;
      try {
        if (!worldLoaded) return;
        const code = getCode() ?? "";
        if (finishedCallback) that.playCallback = finishedCallback;

        if (compileEngine === null || stepCode !== code) {
          await that.stopButton();
          if (finishedCallback) that.playCallback = finishedCallback;
          resetOutput();
          compileEngine = getCompiler();
          stepCode = code;
          try {
            compileEngine.compile(code, inputs);
          } catch (compilerError) {
            if (!silent) showErrorToast(compilerError);
            failCompile();
            return;
          }
        }

        try {
          const done = compileEngine.executeStep();
          draw();
          if (done) finishStep(false);
        } catch (karelError) {
          if (!silent) showErrorToast(karelError);
          finishStep(true);
        }
      } finally {
        stepBusy = false;
      }
    },

    async changeWorld(fileName: string): Promise<void> {
      worldName = fileName;
      if (imagesReady) {
        await loadWorld(fileName);
      }
    },

    stepMove: () => step(karel.move),
    stepTurnLeft: () => step(karel.turnLeft),
    stepTurnRight: () => step(karel.turnRight),
    stepPutBeeper: () => step(karel.putBeeper),
    stepPickBeeper: () => step(karel.pickBeeper),

    getModel: () => karel.getModel(),

    setCode(code: string): void {
      getEditor()?.setValue(code);
    },

    getCode: () => getCode(),

    setSilent(newSilent: boolean): void {
      silent = newSilent;
    },

    setLanguage(newLanguage: "js" | "python"): void {
      language = newLanguage;
    },

    setInputs(newInputs: string[]): void {
      inputs = [...newInputs];
    },

    getOutput: () => [...outputLines],

    destroy(): void {
      if (heartbeatId !== null) {
        clearInterval(heartbeatId);
        heartbeatId = null;
      }
      animating = false;
    },

    async runUnitTest(
      inputWorld: string,
      outputWorld: string | null,
      callback: (passed: boolean) => void,
      testOptions: { inputs?: string[]; expectedOutput?: string } = {},
    ): Promise<void> {
      const tempIde = KarelIde(getEditor, null, inputWorld, { language });
      await tempIde.ready;
      if (testOptions.inputs) tempIde.setInputs(testOptions.inputs);
      const goalModel = outputWorld ? buildModel(outputWorld) : null;
      const simulationOver = (error?: unknown): void => {
        const modelPassed = goalModel
          ? tempIde.getModel().equals(goalModel)
          : true;
        const outputPassed =
          testOptions.expectedOutput !== undefined
            ? tempIde.getOutput().join("\n") === testOptions.expectedOutput
            : true;
        callback(modelPassed && outputPassed && !error);
      };
      tempIde.setSilent(true);
      await tempIde.runCode(simulationOver);
      tempIde.destroy();
    },

    async runSpecificCode(
      code: string,
      finishedCallback: (error?: unknown) => void,
    ): Promise<void> {
      await that.stopButton();

      resetOutput();
      compileEngine = getCompiler();
      stepCode = code;
      try {
        compileEngine.compile(code, inputs);
      } catch (compilerError) {
        if (!silent) showErrorToast(compilerError);
        clearEngine();
        finishedCallback(true);
        return;
      }

      if (silent) {
        runCodeNoDisplay(finishedCallback);
        clearEngine();
        return;
      }

      that.playCallback = finishedCallback;
      animating = true;
    },

    async runCode(finishedCallback?: (error?: unknown) => void): Promise<void> {
      if (!worldLoaded) throw new Error("TRIED TO RUN BEFORE WORLD LOADED");
      const code = getCode();
      if (code == null) return;
      await that.runSpecificCode(code, finishedCallback ?? (() => {}));
    },
  };

  function init(): void {
    if (canvas) {
      canvas.width = canvasModel.getWidth();
      canvas.height = canvasModel.getHeight();
    }
    if (!karelImages.haveCalledLoadImages()) {
      karelImages.loadImages(imagesLoaded);
    } else if (!karelImages.haveLoadedAllImages()) {
      karelImages.addListener(imagesLoaded);
    } else {
      imagesLoaded();
    }
    heartbeatId = setInterval(heartbeat, HEART_BEAT);
  }

  function runCodeNoDisplay(finishedCallback: (error?: unknown) => void): void {
    try {
      while (true) {
        const done = compileEngine!.executeStep();
        if (done) {
          finishedCallback(false);
          break;
        }
      }
    } catch {
      finishedCallback(true);
    }
  }

  function getCompiler(): KarelEngine {
    return KarelEvalEngine(karel, {
      language,
      onOutput: (text) => {
        outputLines.push(text);
        options.onOutput?.([...outputLines]);
      },
    });
  }

  function resetOutput(): void {
    outputLines = [];
    options.onOutput?.([]);
  }

  /** Stops auto-advance and discards the staged/active program. */
  function clearEngine(): void {
    animating = false;
    compileEngine = null;
    stepCode = null;
  }

  /** Delivers the pending play callback (if any) with `error`, then clears it. */
  function settlePlayback(error: boolean): void {
    const callback = that.playCallback;
    that.playCallback = undefined;
    callback?.(error);
  }

  function failCompile(): void {
    clearEngine();
    settlePlayback(true);
  }

  function finishStep(error: boolean): void {
    clearEngine();
    settlePlayback(error);
  }

  function step(stepFunction: () => void): void {
    try {
      stepFunction();
    } catch (msg) {
      showErrorToast(msg);
    }
    draw();
  }

  function imagesLoaded(): void {
    imagesReady = true;
    void loadWorld(worldName).then(() => readyResolve());
  }

  function worldFileLoaded(text: string): void {
    karel.loadWorld(text, canvasModel);
    draw();
    worldLoaded = true;
  }

  async function loadWorld(name: string): Promise<void> {
    const fileName = name + ".w";
    if (!imagesReady) {
      alert("load world called before images ready");
    }
    if (fileName === "15x15.w") {
      worldFileLoaded("Dimension: (15,15)");
      return;
    }
    if (fileName === "null.w") {
      throw new Error("no world name provided");
    }
    worldFileLoaded(requireWorldText(name));
  }

  /** Builds a standalone model for a world without spinning up a full IDE. */
  function buildModel(name: string): KarelModelShape {
    return loadWorldModel(name, CanvasModel(1, 1));
  }

  function heartbeat(): void {
    if (animating) {
      update();
      draw();
    }
  }

  function update(): void {
    actionCountdown = actionCountdown - 1;
    if (actionCountdown === 0) {
      try {
        animating = !compileEngine!.executeStep();
        if (!animating) {
          clearEngine();
          settlePlayback(false);
        }
        numActions += 1;
      } catch (karelError) {
        if (!silent) showErrorToast(karelError);
        clearEngine();
        numActions = 0;
        settlePlayback(true);
      }
      actionCountdown = ACTION_HEARTBEATS;
    }
  }

  function draw(): void {
    if (canvas && context) {
      clear();
      karel.draw(context);
    }
  }

  function clear(): void {
    if (!context) return;
    context.clearRect(0, 0, canvasModel.getWidth(), canvasModel.getHeight());
  }

  function getCode(): string | null {
    return getEditor()?.getValue() ?? null;
  }

  void refreshCountdown;
  void numActions;

  init();
  return that;
}
